import { AuthClient } from '@/src/api/AuthClient';
import { SimplifiedJamClient } from '@/src/api/sdk/SimplifiedJamClient';
import { config } from '@/src/config/app-config';
import { waitForWafIntegration } from '@/src/utils/wafIntegration.util';
import { Jam } from '@amzn/aws-jam-typescript-client';
import { HttpRequest } from '@smithy/protocol-http';
import { omit } from 'lodash';

export interface JamServiceError extends Error {
  $fault: string;
  $metadata: {
    httpStatusCode: number;
    attempts: number;
    totalRetryDelay: number;
  };
  name: string;
  errorMessage: string;
  httpStatusMessage: string;
  errorCode: number;
  httpStatusCode: number;
  message: string;
}

const blockedHeaders = ['x-amz-user-agent', 'amz-sdk-request', 'amz-sdk-invocation-id'];

export const getJamClient = (authClient: AuthClient) => {
  const apiKeyProvider = async () => {
    const authToken = await authClient.getIdToken();

    if (!authToken) {
      throw new Error('Authorization token missing');
    }

    return { apiKey: authToken };
  };

  const handler = async (request: HttpRequest, requestOptions?: any) => {
    const port = request.port ? `:${request.port}` : '';
    const url = `${request.protocol}//${request.hostname}${port}${request.path}`;

    await waitForWafIntegration();
    const wafIntegration = window.AwsWafIntegration as NonNullable<typeof window.AwsWafIntegration>;

    const response = await wafIntegration.fetch(url, {
      headers: omit(request.headers, blockedHeaders),
      method: request.method,
      body: request.body,
      signal: requestOptions?.abortSignal,
    });

    const headers: Record<string, string> = {};
    response.headers.forEach((value, key) => {
      headers[key] = value;
    });

    return {
      response: {
        statusCode: response.status,
        reason: response.statusText,
        headers,
        body: response.body,
      },
    };
  };

  const jamApi = new Jam({
    endpoint: config.api.origin,
    region: 'us-west-2',
    apiKey: apiKeyProvider,
    maxAttempts: 1,
    requestHandler: {
      handle: handler,
    },
  });

  return new SimplifiedJamClient(jamApi);
};

export const getErrorMessage = (e: Error) => {
  return isJamServiceError(e) ? e.errorMessage : e.message;
};

export const isJamServiceError = (e: Error): e is JamServiceError => {
  return (e as JamServiceError).httpStatusCode !== undefined;
};
